/* eslint-disable no-await-in-loop */
import { type ComponentType, lazy } from 'react'

import getGlobal from '../getGlobal'
import createLogger from '../Logging'
import safeStorage from '../safeStorage'

const logger = createLogger('lazyWithRetries')
const RELOADED_CHUNK_KEY = 'aroyaChunkReloaded'
const moduleLoadFailure =
  /(?:failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module)/i

// React.lazy itself constrains its component type with any props; keep the same
// constraint here so the wrapped importer stays assignable to lazy's contract.
type Importer<T extends ComponentType<any>> = () => Promise<{ default: T }>

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error))

const getRetryUrl = (message: string): URL | null => {
  const urlText = message.match(/https?:\/\/[^\s'"<>]+/)?.[0].replace(/[),.;]+$/, '')
  if (!urlText) return null

  try {
    return new URL(urlText)
  } catch {
    return null
  }
}

/** Retry a browser-cached module-fetch failure using a cache-busted chunk URL. */
export const importWithRetries = async <T>(importer: () => Promise<T>): Promise<T> => {
  try {
    return await importer()
  } catch (error) {
    const message = getErrorMessage(error)
    if (!moduleLoadFailure.test(message)) throw error

    const url = getRetryUrl(message)
    // Without a URL, retrying the same importer only repeats the cached rejection.
    // Let the caller's error boundary handle the failure immediately.
    if (!url) throw error

    for (let attempt = 0; attempt < 5; attempt += 1) {
      const delay = 250 * 2 ** attempt
      await new Promise(resolve => setTimeout(resolve, delay))

      url.searchParams.set('t', `${Date.now()}`)
      try {
        return await import(/* @vite-ignore */ url.href)
      } catch {
        logger.debug('retrying import after', delay, 'milliseconds')
      }
    }

    // Keep the original browser message (including the chunk URL) so the lazy
    // wrapper can decide whether a one-time page reload can recover the route.
    throw error
  }
}

/** Retry a missing lazy chunk, then reload once to pick up a newer deployed build. */
export const lazyWithRetries = <T extends ComponentType<any>>(importer: Importer<T>) => {
  const retryImport = async () => {
    try {
      return await importWithRetries(importer)
    } catch (error) {
      const message = getErrorMessage(error)
      const url = moduleLoadFailure.test(message) ? getRetryUrl(message) : null
      // A reload cannot help if the browser did not identify the missing chunk.
      if (!url) throw error

      // One reload can recover when this tab is running a build whose assets have
      // been replaced. Store the chunk URL to prevent a reload loop after refresh.
      const chunk = url.href
      const session = safeStorage('session')
      if (session.getItem(RELOADED_CHUNK_KEY) !== chunk) {
        session.setItem(RELOADED_CHUNK_KEY, chunk)
        getGlobal()?.location.reload()
        // Keep Suspense on its fallback until the reload lands, instead of flashing the error page.
        return new Promise<never>(() => {})
      }

      throw error
    }
  }

  return Object.assign(lazy(retryImport), { $importer: importer })
}
